<template>
    <router-link :to="linkToDocumentationDetailPage" :data-qs="`doc-list-card-${document.id}`">
        <ListCard
            :title="document.title"
            :highlights="highlightsForListCard"
            :locale-tag="document.language"
            :index="index"
            :fallback-icon="fallbackIcon"
        >
            <!-- @vue-ignore -->
            <template v-if="document.rootContentMapTitle" #subtitle>
                <div class="col subtitle light-text-color qit-text-ellipsis">
                    <!-- @vue-ignore -->
                    {{ $t("documentation.in {path}", { path: document.rootContentMapTitle }) }}
                </div>
            </template>
            <template #content>
                <div v-if="document.propertyItems" class="row properties">
                    <PropertyView :items="document.propertyItems" :max-property-width="180" />
                </div>
            </template>
            <template v-if="slots.buttons">
                <slot name="buttons" />
            </template>
            <template v-if="!slots.buttons" #buttons>
                <AddToCollectionButton
                    color="secondary"
                    class="q-mt-md q-mr-md"
                    @click="onOpenOverlayRight"
                    :data-qs="`document-open-add-to-collection-button-${document.id}`"
                />
                <MoreMenu :entries="moreMenuEntries" />
            </template>
        </ListCard>
    </router-link>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */ // TODO: Remove this disable and fix all any types in this file
import { computed, toRefs, useSlots } from "vue";
import { AccessAction, AccessResource } from "@/shared/access-control/access-control";
import { MenuTextEntry } from "@/shared/components/menu/menu.model";
import { useI18n } from "vue-i18n";
import MoreMenu from "@/shared/components/menu/more-menu.vue";
import PropertyView from "@/shared/components/views/property-view.vue";
import { useDocumentRoute } from "@/shared/router/document-route";
import ListCard from "@/shared/components/card/list-card.vue";
import { DocumentListCardProps } from "@/shared/documentation/document-list-card.types.ts";
import { useHasAccess } from "@/shared/access-control/composables/use-has-access.ts";
import AddToCollectionButton from "@/shared/collections/add-to-collection-button.vue";
import { getDocumentIcon } from "@/shared/documentation/icon.utils.ts";
import { Highlights } from "@/shared/documentation/documentation.types.ts";
import { getHighlightedWords } from "@/shared/documentation/highlights.utils.ts";

const props = defineProps<DocumentListCardProps>();

const emit = defineEmits<{
    (e: "toggleCollectionOverlay", topicId: string, contentMapId: string): void;
    (e: "delete", data: { topicId?: string; contentMapId: string }): void;
}>();

const { document } = toRefs(props);
const slots = useSlots();
const { t } = useI18n();
const { getPathToDocumentationDetailPageForSearch } = useDocumentRoute();

const isTopic = computed(() => {
    return document.value.type === "Topic";
});

const onOpenOverlayRight = () => {
    emit("toggleCollectionOverlay", document.value.topicId ?? "", document.value.contentMapId ?? "");
};

const onDelete = () => {
    emit("delete", {
        topicId: document.value.topicId ?? "",
        contentMapId: document.value.contentMapId ?? "",
    });
};

const fallbackIcon = computed(() => {
    return getDocumentIcon(document.value.type);
});

const highlightsForListCard = computed<Highlights>(() => {
    return { text: document.value.highlightText, title: document.value.highlightTitle?.[0] };
});

const highlightsForDetailPage = computed(() => {
    if (
        isTopic.value &&
        (document.value.mimeType === "application/pdf" || document.value.mimeType === "application/xhtml+xml")
    ) {
        const highlightedWords = getHighlightedWords(document.value.highlightText ?? []);
        return {
            title: document.value.highlightTitle?.[0] ?? undefined,
            text: highlightedWords.words.length > 0 ? highlightedWords.words : undefined,
        };
    }

    return { title: undefined, text: undefined };
});

const linkToDocumentationDetailPage = getPathToDocumentationDetailPageForSearch(
    document.value.contentMapId,
    document.value.topicId,
    { context: props.context, products: document.value.products, highlights: highlightsForDetailPage.value }
);

const { hasAccess: canWriteToCollection } = useHasAccess({
    resource: AccessResource.collections,
    action: AccessAction.write,
    externalContext: props.collectionContext,
});
const { hasAccess: canDeleteContent } = useHasAccess({
    resource: AccessResource.contents,
    action: AccessAction.delete,
    externalContext: props.collectionContext,
});

const moreMenuEntries = computed(() => {
    if (!props.moreMenu) return [];

    const entries: MenuTextEntry[] = [];
    if (canWriteToCollection.value) {
        entries.push({
            id: "add-to-collection",
            text: t("documentation.Add to another collection"),
            click: (e?: any) => {
                e?.event?.preventDefault();
                onOpenOverlayRight();
            },
        });
    }

    if (canDeleteContent.value) {
        entries.push({
            id: "delete",
            text: t("documentation.Delete"),
            click: onDelete,
        });
    }

    return entries;
});
</script>

<style lang="scss" scoped>
.properties {
    column-gap: $spacing-xxl;
}
</style>
